import React, { useEffect, useRef } from 'react';
import { startNoise, pickNoiseColor, prefersReducedMotion } from '../../utils/noise';

interface StaticTransitionProps {
    isVisible: boolean;
    onTransitionComplete?: () => void;
    duration?: number;
}

/**
 * Full-viewport static that covers a page while it settles.
 *
 * Skipped entirely under prefers-reduced-motion: a full-screen field of
 * high-contrast random pixels redrawing ten times a second is the one
 * effect on this site that is a genuine risk for photosensitive visitors
 * rather than a matter of taste.
 */
const StaticTransition: React.FC<StaticTransitionProps> = ({
    isVisible,
    onTransitionComplete,
    duration = 1000
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const selectedColorRef = useRef(pickNoiseColor());
    const reduceMotion = prefersReducedMotion();

    useEffect(() => {
        if (reduceMotion) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        let stop = () => {};

        const sizeAndStart = () => {
            stop();
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            stop = startNoise(canvas, { color: selectedColorRef.current });
        };

        sizeAndStart();
        window.addEventListener('resize', sizeAndStart);

        return () => {
            window.removeEventListener('resize', sizeAndStart);
            stop();
        };
    }, [reduceMotion]);

    // Stop painting as soon as the overlay is transparent — the previous
    // version kept its rAF loop running for the life of the page even
    // once the canvas was invisible and pointer-events: none.
    useEffect(() => {
        if (reduceMotion || isVisible) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        const timer = setTimeout(() => {
            if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        }, duration);
        return () => clearTimeout(timer);
    }, [isVisible, duration, reduceMotion]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleTransitionEnd = () => {
            if (!isVisible && onTransitionComplete) {
                onTransitionComplete();
            }
        };

        container.addEventListener('transitionend', handleTransitionEnd);
        return () => container.removeEventListener('transitionend', handleTransitionEnd);
    }, [isVisible, onTransitionComplete]);

    if (reduceMotion) return null;

    return (
        <div
            ref={containerRef}
            aria-hidden="true"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 9999,
                opacity: isVisible ? 1 : 0,
                pointerEvents: isVisible ? 'all' : 'none',
                transition: `opacity ${duration}ms ease-in-out`
            }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    width: '100%',
                    height: '100%'
                }}
            />
        </div>
    );
};

export default StaticTransition;
